import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class InfiniteScrollService implements OnDestroy {
  private observer: IntersectionObserver;
  private loadingSubject = new Subject<boolean>();
  private destroy$ = new Subject<void>();
  private isProcessing = false;
  private debounceTime = 300;

  constructor() {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  private setupIntersectionObserver(root: Element): void {
    this.observer = new IntersectionObserver(
      entries => this.handleIntersect(entries),
      {
        root: root ?? null,
        rootMargin: '0px 0px 50px 0px',
        threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1.0],
      }
    );
  }

  observe(root: Element, target: Element): void {
    if (target) {
      this.setupIntersectionObserver(root);
      this.observer?.observe(target);
    }
  }

  unobserve(target: Element): void {
    if (target) {
      this.observer?.unobserve(target);
    }
  }

  private handleIntersect(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.isProcessing) {
        this.isProcessing = true;
        this.loadingSubject.next(true);

        const debouncedAction = new Observable<void>(subscriber => {
          subscriber.next();
          subscriber.complete();
        }).pipe(debounceTime(this.debounceTime));

        debouncedAction.pipe(takeUntil(this.destroy$)).subscribe({
          complete: () => {
            this.isProcessing = false;
            this.loadingSubject.next(false);
          },
        });
      }
    });
  }

  reset(): void {
    this.isProcessing = false;
    this.loadingSubject.next(false);
  }
}
